import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormGroup } from '../../../../../../components/form/FormGroup';
import { useCurrencyFormatter } from '../../../../../../hooks/useCurrencyFormatter';
import { formatDateShort } from '../../../../../../utils/dateUtils';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { useExpenseByIdQuery } from '../../../../__generated__/graphql';
import ServerError from '../../../../../../components/ServerError';
import Skeleton from 'react-loading-skeleton';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';

type Props = {
  expenseId: string;
};

const EMPTY_VALUE = '---';
const i18Path = 'expenses.expenseSideBar';

export const ExpenseDetails: React.FC<Props> = ({ expenseId }) => {
  const t = useNamespacedTranslation(i18Path);
  const formatCurrency = useCurrencyFormatter();

  const { data, loading, error } = useExpenseByIdQuery({
    variables: { id: expenseId },
  });

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ServerError />;
  }

  const expense = data?.expense;

  if (!expense) {
    return <ServerError />;
  }

  return (
    <div className="mt-4">
      <FormGroup>
        <Form.Label className="fw-bold">{t('expenseDescription')}</Form.Label>
        <p>{expense.flair__Description__c}</p>
      </FormGroup>

      <Row>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">{t('amount')}</Form.Label>
            <p>
              {formatCurrency({
                value: expense?.flair__Final_Amount__c,
                currency: expense?.CurrencyIsoCode,
              })}
            </p>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">{t('category')}</Form.Label>
            <p>{expense?.expenseCategory?.Name ?? EMPTY_VALUE}</p>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">{t('expenseDate')}</Form.Label>
            <p>
              {expense.flair__Date_From__c &&
                formatDateShort(expense.flair__Date_From__c)}
            </p>
          </FormGroup>
        </Col>
      </Row>

      {expense?.project && (
        <Row>
          <Col>
            <FormGroup>
              <Form.Label className="fw-bold">{t('project')}</Form.Label>
              <p>{expense.project?.flair__Name__c ?? EMPTY_VALUE}</p>
            </FormGroup>
          </Col>
        </Row>
      )}

      {expense.ContentDocumentLinks &&
      expense.ContentDocumentLinks.length > 0 ? (
        <Row>
          <Col>
            <FormGroup>
              <Form.Label className="fw-bold">
                {t('invoiceAttached')}
              </Form.Label>
              {expense.ContentDocumentLinks.map((document) => {
                const fileName = document.ContentDocument.FileExtension
                  ? `${document.ContentDocument.Title}.${document.ContentDocument.FileExtension}`
                  : document.ContentDocument.Title;
                return (
                  <div key={document.Id}>
                    <a
                      target="_blank"
                      rel="noreferrer noopener"
                      href={document.downloadUrl || undefined}>
                      {fileName}
                      <span className="ms-2">
                        <FlairIcon size="xs" icon="external-link-outline" />
                      </span>
                    </a>
                  </div>
                );
              })}
            </FormGroup>
          </Col>
        </Row>
      ) : (
        ''
      )}
    </div>
  );
};

const Loading: React.FC = () => {
  return (
    <div>
      <Skeleton height={80} />
      <Skeleton height={80} />
      <Skeleton height={80} />
    </div>
  );
};
